import React, { useRef } from 'react';
import { toast } from 'sonner';
import { ImagePlus, Link2, Lock, Printer, FileText, Trash2, X, LogOut, MousePointerClick, Crown, Hash, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { readLogo } from '@/lib/bracket/custom';

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">{children}</label>
);

const ClearBtn = ({ onClick, label, testId }) => (
  <button type="button" onClick={onClick} aria-label={label} data-testid={testId}
    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 hover:text-[#CF142B] transition-colors"><X className="w-4 h-4" /></button>
);

const LogoField = ({ logo, onChange }) => {
  const ref = useRef(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Choose an image file (PNG, JPG or SVG)'); return; }
    try { onChange(await readLogo(file)); } catch { toast.error('That image could not be read'); }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="w-14 h-14 rounded-lg border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
        {logo ? <img src={logo} alt="Logo" className="w-full h-full object-contain" data-testid="custom-logo-preview" /> : <ImagePlus className="w-5 h-5 text-slate-300" />}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} data-testid="custom-logo-upload-btn">
          {logo ? 'Replace logo' : 'Upload logo'}
        </Button>
        {logo && <Button type="button" variant="ghost" size="sm" className="text-slate-500" onClick={() => onChange('')} data-testid="custom-logo-remove">Remove</Button>}
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="custom-logo-input" />
    </div>
  );
};

const TeamInputs = ({ slots, names, setName }) => (
  <div className="flex flex-col gap-2 max-h-[340px] overflow-y-auto pr-1 -mr-1" data-testid="custom-team-list">
    {slots.map((s, i) => (
      <div key={s.key} className="relative flex items-center gap-2">
        <span className="w-6 text-right text-xs font-bold text-slate-400 tabular-nums">{i + 1}</span>
        <div className="relative flex-1">
          <Input value={names[s.key] || ''} onChange={(e) => setName(s.key, e.target.value)} placeholder={s.label} maxLength={60}
            className="h-9 pr-8" data-testid={`custom-team-input-${s.key.slice(5)}`} />
          {names[s.key] && <ClearBtn onClick={() => setName(s.key, '')} label={`Clear ${s.label}`} testId={`custom-team-clear-${s.key.slice(5)}`} />}
        </div>
      </div>
    ))}
  </div>
);

const LaterRounds = ({ entries, setName, labelOf }) => (
  entries.length ? (
    <div className="flex flex-wrap gap-1.5" data-testid="custom-later-list">
      {entries.map(([k, v]) => (
        <span key={k} className="inline-flex items-center gap-1 rounded-full bg-slate-100 pl-2.5 pr-1 py-1 text-xs text-slate-700">
          <span className="text-slate-400">{labelOf(k)}:</span><span className="font-semibold max-w-[110px] truncate">{v}</span>
          <button type="button" onClick={() => setName(k, '')} aria-label={`Remove ${v}`} className="rounded-full p-0.5 hover:bg-white hover:text-[#CF142B]"><X className="w-3 h-3" /></button>
        </span>
      ))}
    </div>
  ) : <p className="text-xs text-slate-400">None yet.</p>
);

export const CustomizePanel = ({ c, set, setName, slots, labelOf, isPro, onExport, onExit, onClearAll, busy, teamTools, onShare, shared, syncStatus, onManage }) => {
  const later = Object.entries(c.names).filter(([k, v]) => v && !k.startsWith('team:'));
  return (
    <aside className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 flex flex-col gap-5 xl:sticky xl:top-20 animate-in fade-in slide-in-from-left-2 duration-300"
      data-testid="bracket-customize-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#00247D] leading-none" style={{ fontFamily: 'Barlow Condensed' }}>CUSTOMIZE BRACKET</h2>
          <p className="text-xs text-slate-500 mt-1">Changes show on the bracket as you type and are saved in this browser. Your logo is only uploaded if you share the bracket.</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onExit} className="text-slate-500 -mr-2" data-testid="custom-exit-btn"><LogOut className="w-4 h-4 mr-1" />Exit</Button>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="custom-title">Tournament title</Label>
        <div className="relative">
          <Input id="custom-title" value={c.title} maxLength={80} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. Spring Classic 2026" className="pr-8" data-testid="custom-title-input" />
          {c.title && <ClearBtn onClick={() => set({ title: '' })} label="Clear title" testId="custom-title-clear" />}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="custom-date">Date <span className="normal-case tracking-normal font-medium">(optional)</span></Label>
        <div className="relative">
          <Input id="custom-date" type="date" value={c.date} onChange={(e) => set({ date: e.target.value })} className="pr-8" data-testid="custom-date-input" />
          {c.date && <ClearBtn onClick={() => set({ date: '' })} label="Clear date" testId="custom-date-clear" />}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Logo <span className="normal-case tracking-normal font-medium">(optional)</span></Label>
        <LogoField logo={c.logo} onChange={(logo) => set({ logo })} />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Label>Starting teams</Label>
          {teamTools}
        </div>
        <TeamInputs slots={slots} names={c.names} setName={setName} />
      </div>

      <div className="flex flex-col gap-2">
        <Label>Later rounds &amp; champion</Label>
        <p className="text-xs text-slate-500 flex items-start gap-1.5"><MousePointerClick className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#CF142B]" />
          Click any spot on the bracket, including later rounds and the champion box, to type a name there.</p>
        <p className="text-xs text-slate-500 flex items-start gap-1.5" data-testid="custom-scores-hint"><Hash className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#CF142B]" />
          <span>Click a game's score box to enter its score{isPro ? '' : ' (Pro)'}. Winners move into the next round.</span></p>
        <LaterRounds entries={later} setName={setName} labelOf={labelOf} />
      </div>

      <div className="border-t border-slate-100 pt-4 flex flex-col gap-2">
        <Button onClick={onManage} className="w-full bg-[#CF142B] hover:bg-[#A81022] font-bold transition-[background-color,transform] active:scale-[0.98]" data-testid="custom-manage-btn">
          {isPro ? <Trophy className="w-4 h-4 mr-2" /> : <Lock className="w-4 h-4 mr-2" />}Manage as Tournament
        </Button>
        <p className="text-[11px] text-slate-500 -mt-0.5">Turns this bracket into a real tournament in My Schedules with game times, live scores, stats and box scores.</p>
        <Button variant="outline" onClick={onShare} className="w-full border-[#00247D]/40 text-[#00247D] hover:bg-blue-50" data-testid="custom-share-btn">
          <Link2 className="w-4 h-4 mr-2" />{shared ? 'Share link' : 'Get share link'}
        </Button>
        {shared && (
          <p className="text-[11px] text-slate-500 flex items-center gap-1.5 -mt-0.5" data-testid="custom-share-status">
            <span className={`w-1.5 h-1.5 rounded-full ${syncStatus === 'error' ? 'bg-[#CF142B]' : 'bg-emerald-500'}`} />
            {syncStatus === 'saving' ? 'Updating your share link…' : syncStatus === 'error' ? 'Share link not updated yet, retrying on your next change' : 'Share link is live and up to date'}
          </p>
        )}
        {!isPro && (
          <p className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="custom-pro-note">
            <Crown className="w-4 h-4 text-amber-500 shrink-0" />Previewing is free. Printing or downloading your customized bracket needs Pro.
          </p>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={() => onExport('pdf')} disabled={!!busy} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="custom-download-btn">
            {isPro ? <FileText className="w-4 h-4 mr-2" /> : <Lock className="w-4 h-4 mr-2" />}Download PDF
          </Button>
          <Button variant="outline" onClick={() => onExport('print')} disabled={!!busy} data-testid="custom-print-btn">
            {isPro ? <Printer className="w-4 h-4 mr-2" /> : <Lock className="w-4 h-4 mr-2" />}Print
          </Button>
        </div>
        <Button variant="ghost" size="sm" onClick={onClearAll} className="text-slate-500 self-start" data-testid="custom-clear-all-btn">
          <Trash2 className="w-4 h-4 mr-1.5" />Clear everything
        </Button>
      </div>
    </aside>
  );
};
